/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B10050 社員マスタメンテ
 */
import {
  B000120EigyoNm,
  B000130BumonNm,
  B000230KengenNm,
  catchRestError,
} from "clients/rest";
import {
  B100050Create,
  B100050SanshoShain,
  B100050Shain,
  B100050Update,
} from "clients/rest/b100/B100050";
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  msgState,
  SNormalButton,
  SFileButton,
  SConfirmDialog,
} from "components";
import { getClientMessage } from "messages";
import {
  B100050Mode,
  getSCode,
  SakujoyoteiKbn,
  SCode,
  SeibetsuKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { B100910 } from "pages";
import { B000120 } from "pages/b000/B000120";
import { B000130 } from "pages/b000/B000130";
import { B000140 } from "pages/b000/B000140";
import { B000230 } from "pages/b000/B000230";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  modeKbn: SCode;
  shainCd: string;
  sanshoShainCd: string;
  shainNm: string;
  eigyoCd: string;
  eigyoNm: string;
  shainKana: string;
  bumonCd: string;
  bumonNm: string;
  naisenNo: string;
  keitaiNo: string;
  mailaddress: string;
  seibetsuKbn: SCode;
  password: string;
  seinenYmd: string | null;
  nyushaYmd: string | null;
  kengenId: string;
  kengenNm: string;
  taishaYmd: string | null;
  sakujoyoteiKbn: SCode;
  version: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  modeKbn: getSCode(B100050Mode, "0"),
  shainCd: "",
  sanshoShainCd: "",
  shainNm: "",
  eigyoCd: "",
  eigyoNm: "",
  shainKana: "",
  bumonCd: "",
  bumonNm: "",
  naisenNo: "",
  keitaiNo: "",
  mailaddress: "",
  seibetsuKbn: getSCode(SeibetsuKbn, "0"),
  password: "",
  seinenYmd: null,
  nyushaYmd: null,
  kengenId: "",
  kengenNm: "",
  taishaYmd: null,
  sakujoyoteiKbn: getSCode(SakujoyoteiKbn, "0"),
  version: "",
};

// 固定値
const dummyPassword = "●●●●●";

// モード(新規)
const modeNew = "0";

const focusId = "B100050";

// B100050 社員マスタメンテ
export const B100050 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 遷移元データ取得用
  const navigate = useNavigate();
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 活性設定
  // 新規モードかどうか
  const isNew = methods.watch("modeKbn")?.value === modeNew;

  // 初期表示イベント
  useEffect(() => {
    // unregisterを使用する値をresetで初期化(defalutValueが無視され、初期値にundifindが設定されることがあるため)
    methods.reset();
    methods.reset({
      ...methods.getValues(),
      shainCd: "",
      sanshoShainCd: "",
    });

    focus(1);
  }, []);

  // モード変更時
  const handleChangeModeKbn = () => {
    // 新規の場合
    if (isNew) {
      methods.reset();
      methods.reset({
        ...methods.getValues(),
        shainCd: "",
        sanshoShainCd: "",
      });
      methods.setValue("modeKbn", getSCode(B100050Mode, "1"));
    }
    // 修正の場合
    else {
      methods.reset();
      methods.reset({
        ...methods.getValues(),
        shainCd: "",
        sanshoShainCd: "",
      });
    }
  };

  // 更新
  const handleClickUpdateButton = () => {
    // 新規の場合
    if (isNew) {
      B100050Create(methods)
        .then(() => {
          methods.reset();
          methods.reset({
            ...methods.getValues(),
            shainCd: "",
            sanshoShainCd: "",
          });
          // ↓これがあると完了メッセージが表示される
          methods.setValue("shainCd", "");
          setMsg(getClientMessage("B100050_001"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
    // 修正の場合
    else {
      B100050Update(methods)
        .then(() => {
          methods.reset();
          methods.reset({
            ...methods.getValues(),
            shainCd: "",
            sanshoShainCd: "",
          });
          // ↓これがあると完了メッセージが表示される
          methods.setValue("shainCd", "");
          setMsg(getClientMessage("B100050_002"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
  };

  // パスワードフォーカス時
  const handleFocusPassword = () => {
    // 新規の場合
    if (isNew) return;

    // パスワードが変更済の場合
    if (methods.formState.dirtyFields.password) return;

    // ダミー文字をクリアする
    methods.setValue("password", "");
  };

  // パスワード変更時
  const handleChangePassword = () => {
    // 新規の場合
    if (isNew) return;

    // パスワードが変更済の場合
    if (methods.formState.dirtyFields.password) return;

    // パスワードにダミーの値をセット
    methods.setValue("password", dummyPassword);
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="社員マスタメンテ"
          gridTemplateColumns="8rem 6rem 2rem 14rem 4rem 8rem 6rem 3rem 3rem 7rem "
        >
          <SText gridColumn="span 8"></SText>
          <SFileButton
            label={labels.buttons.excel}
            gridColumn="span 2"
            align="right"
            {...register(19)}
            dialog={<B100910 gamenId="100050" />}
            dialogAction="close"
          />
          <SLabel required gridColumn="1">
            モード
          </SLabel>
          <SCodeSelect
            name="modeKbn"
            required
            label="モード"
            options={B100050Mode}
            {...register(1)}
            onChange={() => handleChangeModeKbn()}
          />
          <SLabel required gridColumn="1">
            社員コード
          </SLabel>
          {isNew ? (
            <>
              <SNumberField
                name="shainCd"
                required
                label="社員コード"
                code
                gridColumn="span 2"
                size={7}
                {...register(2)}
              />
            </>
          ) : (
            <>
              <SNumberField
                name="shainCd"
                required
                label="社員コード"
                code
                gridColumn="span 2"
                size={7}
                dialog={<B000140 deljogaiKbn="1" />}
                onBlur={() => B100050Shain(methods, navigate, setMsg)}
                handleSubmit={(data: any) => {
                  methods.setValue("shainCd", "");
                  methods.setValue("shainNm", "");
                  methods.setValue("eigyoCd", "");
                  methods.setValue("eigyoNm", "");
                  methods.setValue("shainKana", "");
                  methods.setValue("bumonCd", "");
                  methods.setValue("bumonNm", "");
                  methods.setValue("naisenNo", "");
                  methods.setValue("keitaiNo", "");
                  methods.setValue("mailaddress", "");
                  methods.setValue("seibetsuKbn", null);
                  methods.setValue("password", "");
                  methods.setValue("seinenYmd", null);
                  methods.setValue("nyushaYmd", null);
                  methods.setValue("kengenId", "");
                  methods.setValue("kengenNm", "");
                  methods.setValue("taishaYmd", null);
                  methods.setValue("sakujoyoteiKbn", null);

                  methods.setValue("shainCd", data?.shainCd);
                  methods.setValue("shainNm", data?.shainNm);
                  methods.setValue("eigyoCd", data?.eigyoCd);
                  methods.setValue("shainKana", data?.shainKana);
                  methods.setValue("bumonCd", data?.bumonCd);
                  methods.setValue("naisenNo", data?.naisenNo);
                  methods.setValue("keitaiNo", data?.keitaiNo);
                  methods.setValue("mailaddress", data?.mailaddress);
                  methods.setValue(
                    "seibetsuKbn",
                    getSCode(SeibetsuKbn, data?.seibetsuKbn)
                  );
                  methods.setValue("seinenYmd", data?.seinenYmd);
                  methods.setValue("nyushaYmd", data?.nyushaYmd);
                  methods.setValue("kengenId", data?.kengenId);
                  methods.setValue("kengenNm", data?.kengenNm);
                  methods.setValue("taishaYmd", data?.taishaYmd);
                  methods.setValue(
                    "sakujoyoteiKbn",
                    getSCode(SakujoyoteiKbn, data?.sakujoyoteiKbn)
                  );
                  methods.setValue("version", data?.version);
                  methods.setValue("password", dummyPassword);

                  // 営業所名を取得
                  B000120EigyoNm(methods, navigate, setMsg);
                  // 部門名を取得
                  B000130BumonNm(methods, navigate, setMsg);
                  // 権限名を取得
                  B000230KengenNm(methods, navigate, setMsg);
                }}
                {...register(2)}
              />
            </>
          )}
          <SText gridColumn="5"></SText>
          <SLabel>参照コード</SLabel>
          <SNumberField
            name="sanshoShainCd"
            code
            disabled={!isNew}
            dialog={<B000140 deljogaiKbn="1" />}
            onBlur={() => B100050SanshoShain(methods, navigate, setMsg)}
            handleSubmit={(data: any) => {
              methods.setValue("sanshoShainCd", "");
              methods.setValue("shainNm", "");
              methods.setValue("eigyoCd", "");
              methods.setValue("eigyoNm", "");
              methods.setValue("shainKana", "");
              methods.setValue("bumonCd", "");
              methods.setValue("bumonNm", "");
              methods.setValue("naisenNo", "");
              methods.setValue("keitaiNo", "");
              methods.setValue("mailaddress", "");
              methods.setValue("seibetsuKbn", null);
              methods.setValue("password", "");
              methods.setValue("seinenYmd", null);
              methods.setValue("nyushaYmd", null);
              methods.setValue("kengenId", "");
              methods.setValue("kengenNm", "");
              methods.setValue("taishaYmd", null);
              methods.setValue("sakujoyoteiKbn", null);

              methods.setValue("sanshoShainCd", data?.shainCd);
              methods.setValue("shainNm", data?.shainNm);
              methods.setValue("eigyoCd", data?.eigyoCd);
              methods.setValue("eigyoNm", data?.eigyoNm);
              methods.setValue("shainKana", data?.shainKana);
              methods.setValue("bumonCd", data?.bumonCd);
              methods.setValue("naisenNo", data?.naisenNo);
              methods.setValue("keitaiNo", data?.keitaiNo);
              methods.setValue("mailaddress", data?.mailaddress);
              methods.setValue(
                "seibetsuKbn",
                getSCode(SeibetsuKbn, data?.seibetsuKbn)
              );
              methods.setValue("seinenYmd", data?.seinenYmd);
              methods.setValue("nyushaYmd", data?.nyushaYmd);
              methods.setValue("kengenId", data?.kengenId);
              methods.setValue("kengenNm", data?.kengenNm);
              methods.setValue("taishaYmd", data?.taishaYmd);
              methods.setValue(
                "sakujoyoteiKbn",
                getSCode(SakujoyoteiKbn, data?.sakujoyoteiKbn)
              );

              methods.setValue("password", "");

              // 営業所名を取得
              B000120EigyoNm(methods, navigate, setMsg);
              // 部門名を取得
              B000130BumonNm(methods, navigate, setMsg);
              // 権限名を取得
              B000230KengenNm(methods, navigate, setMsg);
            }}
            size={7}
            {...register(3)}
          />
          <SLabel required gridColumn="1">
            名前
          </SLabel>
          <STextField
            name="shainNm"
            required
            label="名前"
            gridColumn="span 3"
            code
            size={20}
            {...register(4)}
          />
          <SText gridColumn="5"></SText>
          <SLabel required>営業所コード</SLabel>
          <SNumberField
            name="eigyoCd"
            label="営業所コード"
            required
            dialog={<B000120 />}
            onBlur={() => B000120EigyoNm(methods, navigate, setMsg)}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCd", data?.eigyoCd);
              methods.setValue("eigyoNm", data?.eigyoNm);
            }}
            code
            size={6}
            {...register(5)}
          />
          <STextField
            name="eigyoNm"
            label="営業所名"
            gridColumn="span 3"
            size={20}
            disabled
          />
          <SLabel required gridColumn="1">
            カナ名
          </SLabel>
          <STextField
            name="shainKana"
            label="カナ名"
            gridColumn="span 3"
            required
            size={20}
            {...register(6)}
          />
          <SText gridColumn="5"></SText>
          <SLabel required>部門コード</SLabel>
          <SNumberField
            name="bumonCd"
            label="部門コード"
            required
            onBlur={() => B000130BumonNm(methods, navigate, setMsg)}
            dialog={<B000130 />}
            handleSubmit={(data: any) => {
              methods.setValue("bumonCd", data?.bumonCd);
              methods.setValue("bumonNm", data?.bumonNm);
            }}
            code
            size={6}
            {...register(7)}
          />
          <STextField
            name="bumonNm"
            label="部門名"
            gridColumn="span 3"
            size={20}
            disabled
          />
          <SLabel gridColumn="1">性別区分</SLabel>
          <SCodeSelect
            name="seibetsuKbn"
            label="性別区分"
            {...register(8)}
            options={SeibetsuKbn}
          />
          <SText gridColumn="5"></SText>
          <SLabel>内線番号</SLabel>
          <STextField
            name="naisenNo"
            label="内線番号"
            gridColumn="span 3"
            code
            size={20}
            {...register(9)}
          />
          <SLabel gridColumn="1">生年月日</SLabel>
          <SDateField
            name="seinenYmd"
            label="生年月日"
            gridColumn="span 2"
            {...register(10)}
          />
          <SText gridColumn="5"></SText>
          <SLabel>携帯番号</SLabel>
          <STextField
            name="keitaiNo"
            label="携帯番号"
            gridColumn="span 3"
            code
            size={20}
            {...register(11)}
          />
          <SLabel gridColumn="1">入社年月日</SLabel>
          <SDateField
            name="nyushaYmd"
            label="入社年月日"
            gridColumn="span 2"
            {...register(12)}
          />
          <SText gridColumn="5"></SText>
          <SLabel>メールアドレス</SLabel>
          <STextField
            name="mailaddress"
            gridColumn="span 4"
            size={40}
            {...register(13)}
          />
          <SLabel gridColumn="1">退社年月日</SLabel>
          <SDateField
            name="taishaYmd"
            label="退社年月日"
            gridColumn="span 2"
            {...register(14)}
          />
          <SText gridColumn="5"></SText>
          <SLabel required>パスワード</SLabel>
          <STextField
            name="password"
            required
            label="パスワード"
            gridColumn="span 2"
            {...register(15)}
            onFocus={() => handleFocusPassword()}
            onBlur={() => handleChangePassword()}
          />
          <SLabel gridColumn="1">削除予定区分</SLabel>
          <SCodeSelect
            name="sakujoyoteiKbn"
            {...register(16)}
            options={SakujoyoteiKbn}
          />
          <SText gridColumn="5"></SText>
          <SLabel required>権限ID</SLabel>
          <SNumberField
            name="kengenId"
            required
            label="権限ID"
            code
            dialog={<B000230 />}
            handleSubmit={(data: any) => {
              methods.setValue("kengenId", data?.kengenId);
              methods.setValue("kengenNm", data?.kengenNm);
            }}
            size={20}
            onBlur={() => B000230KengenNm(methods, navigate, setMsg)}
            {...register(17)}
          />
          <STextField name="kengenNm" gridColumn="span 2" size={10} disabled />
          <SText gridColumn="span 9"></SText>
          <SNormalButton
            label={labels.buttons.update}
            align="right"
            {...register(18)}
            dialog={
              <SConfirmDialog
                title="マスタ更新"
                msg="更新します。よろしいですか？"
              />
            }
            dialogAction="confirm"
            handleSubmit={handleClickUpdateButton}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
